import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useSubscriptions } from "../../lib/subscriptions/useSubscriptions";
import { useCategories } from "../../lib/categories/useCategories";
import type {
  NewSubscriptionInput,
  SubscriptionCadence,
  SubscriptionStatus,
} from "../../lib/subscriptions/types";

interface NewSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initialValues?: Partial<NewSubscriptionInput>;
  mode?: "create" | "edit";
  subscriptionId?: string;
}

const CADENCE_OPTIONS: { value: SubscriptionCadence; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS: { value: SubscriptionStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
  { value: "warning", label: "Warning" },
];

function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim().replace(/[$,\s]/g, "");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return null;
  const asFloat = Number.parseFloat(trimmed);
  if (!Number.isFinite(asFloat)) return null;
  return Math.round(asFloat * 100);
}

function centsToInput(cents: number | undefined | null): string {
  if (cents === undefined || cents === null) return "";
  return (cents / 100).toFixed(2);
}

export function NewSubscriptionDialog({
  open,
  onOpenChange,
  initialValues,
  mode = "create",
  subscriptionId,
}: NewSubscriptionDialogProps) {
  const { add, update } = useSubscriptions();
  const { categories } = useCategories();

  const [vendor, setVendor] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [cadence, setCadence] = useState<SubscriptionCadence | "">("");
  const [amountText, setAmountText] = useState("");
  const [lastChargeOn, setLastChargeOn] = useState("");
  const [status, setStatus] = useState<SubscriptionStatus>("active");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      return;
    }
    setVendor(initialValues?.vendor ?? "");
    setCategoryId(initialValues?.categoryId ?? "");
    setCadence(initialValues?.cadence ?? "");
    setAmountText(
      initialValues?.lastChargeCents !== undefined &&
        initialValues?.lastChargeCents !== null
        ? centsToInput(initialValues.lastChargeCents)
        : "",
    );
    setLastChargeOn(initialValues?.lastChargeOn ?? "");
    setStatus(initialValues?.status ?? "active");
    setNotes(initialValues?.notes ?? "");
    setError(null);
    setSubmitting(false);
  }, [open, initialValues]);

  async function handleSubmit() {
    setError(null);
    if (!vendor.trim()) {
      setError("Vendor is required.");
      return;
    }
    let cents: number | null = null;
    if (amountText.trim() !== "") {
      const parsed = parseAmountToCents(amountText);
      if (parsed === null || parsed < 0) {
        setError("Amount must be a positive number (e.g. 99.00).");
        return;
      }
      cents = parsed;
    }

    const selectedCategory = categoryId
      ? categories.find((c) => c.id === categoryId)
      : undefined;

    setSubmitting(true);
    try {
      if (mode === "edit" && subscriptionId) {
        await update(subscriptionId, {
          vendor: vendor.trim(),
          categoryId: categoryId || null,
          categoryLabel: selectedCategory?.label ?? null,
          cadence: cadence || null,
          lastChargeCents: cents,
          lastChargeOn: lastChargeOn || null,
          status,
          notes: notes.trim() || null,
        });
      } else {
        await add({
          vendor: vendor.trim(),
          categoryId: categoryId || null,
          categoryLabel: selectedCategory?.label ?? null,
          cadence: cadence || null,
          lastChargeCents: cents,
          lastChargeOn: lastChargeOn || null,
          status,
          notes: notes.trim() || null,
        });
      }
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Submit failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  const title = mode === "edit" ? "Edit subscription" : "New subscription";
  const description =
    mode === "edit"
      ? "Update the subscription details below."
      : "Track a recurring subscription. Only the vendor name is required.";
  const submitLabel = mode === "edit" ? "Save changes" : "Create subscription";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-subscription-dialog"
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-subscription-vendor"
              className="text-xs font-medium text-slate-700"
            >
              Vendor
            </label>
            <input
              id="new-subscription-vendor"
              data-testid="new-subscription-vendor"
              type="text"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. Figma"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-subscription-category"
                className="text-xs font-medium text-slate-700"
              >
                Category
              </label>
              <select
                id="new-subscription-category"
                data-testid="new-subscription-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              >
                <option value="">None</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-subscription-cadence"
                className="text-xs font-medium text-slate-700"
              >
                Cadence
              </label>
              <select
                id="new-subscription-cadence"
                data-testid="new-subscription-cadence"
                value={cadence}
                onChange={(e) =>
                  setCadence(e.target.value as SubscriptionCadence | "")
                }
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              >
                <option value="">Select…</option>
                {CADENCE_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-subscription-amount"
                className="text-xs font-medium text-slate-700"
              >
                Last charge (USD)
              </label>
              <input
                id="new-subscription-amount"
                data-testid="new-subscription-amount"
                type="text"
                inputMode="decimal"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                placeholder="99.00"
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-subscription-last-charge"
                className="text-xs font-medium text-slate-700"
              >
                Last charge date
              </label>
              <input
                id="new-subscription-last-charge"
                data-testid="new-subscription-last-charge"
                type="date"
                value={lastChargeOn}
                onChange={(e) => setLastChargeOn(e.target.value)}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-subscription-status"
              className="text-xs font-medium text-slate-700"
            >
              Status
            </label>
            <select
              id="new-subscription-status"
              data-testid="new-subscription-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-subscription-notes"
              className="text-xs font-medium text-slate-700"
            >
              Notes
            </label>
            <textarea
              id="new-subscription-notes"
              data-testid="new-subscription-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — e.g. Billed to ops card"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2] resize-none"
            />
          </div>

          {error && (
            <div
              data-testid="new-subscription-error"
              className="text-xs font-medium text-red-600"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="new-subscription-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
